import {
  Button,
  BusyIndicator,
  FlexBox,
  FlexBoxDirection,
  MessageBoxType,
  TextArea,
  TextAreaDomRef,
  Timeline,
  Ui5CustomEvent
} from '@ui5/webcomponents-react';
import { ConfirmationDialog, ConfirmationDialogProps } from 'components/shared/ConfirmationDialog';
import {
  BaseQueryDetailsFragment,
  CommentType,
  Distribution,
  QueryAnswerInput,
  QueryComment,
  QueryCommentInput,
  RequestClarificationInput,
  Status,
  Supplier,
  useAddAnswerToQueryMutation,
  useAddCommentToQueryMutation,
  useRequestClarificationMutation
} from 'generated/graphql';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { CustomTimeline } from './ConversationTimeline';
import { useStyles } from './QueryConversation.jss';
import '@ui5/webcomponents-icons/dist/question-mark';
import '@ui5/webcomponents-icons/dist/lightbulb';
import { ResultUserMessage, UserMessage } from 'components/shared/UserMessage';
import { useEditQueryStore } from 'stores/EditQueryStore';
import { HistoryCommentsSwitch } from './HistoryCommentsSwitch/HistoryCommentsSwitch';

import ButtonDesign from '@ui5/webcomponents/dist/types/ButtonDesign.js';

type BaseQueryComments = Pick<BaseQueryDetailsFragment, 'id' | 'status' | 'comments'> | null;

interface QueryConversationProps {
  query?: BaseQueryComments;
  isInternalUser: boolean;
  setAllowEditModeWithoutConfirmation: (value: boolean) => void;
  suppliers: Supplier[];
  distributions: Distribution[] | undefined;
}

interface ConfirmDialogState {
  open?: CommentType.ANSWER | CommentType.REQUEST_CLARIFICATION;
}

export const QueryConversation: React.FC<QueryConversationProps> = (props) => {
  const { query, isInternalUser, setAllowEditModeWithoutConfirmation, suppliers, distributions } =
    props;
  const classes = useStyles();
  const { t } = useTranslation();

  const { editMode } = useEditQueryStore();

  const [commentText, setCommentText] = useState<string>('');
  const [allowLeavingPage, setAllowLeavingPage] = useState<boolean>(true);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<ConfirmDialogState>({
    open: undefined
  });
  const [isUserMessageOpen, setIsUserMessageOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState<ResultUserMessage>({
    isSuccess: false,
    message: t('display-query-comments-error-saving')
  });

  const [shouldShowHistoryStatusComments, setShouldShowHistoryStatusComments] =
    useState<boolean>(false);

  const [filteredComments, setFilteredComments] = useState<QueryComment[]>([]);

  useEffect(() => {
    if (!query?.comments) {
      setFilteredComments([]);
      return;
    }
    if (shouldShowHistoryStatusComments) {
      setFilteredComments(query.comments as QueryComment[]);
    } else {
      setFilteredComments(
        (query.comments as QueryComment[]).filter((c) => c.commentType !== CommentType.HISTORY)
      );
    }
  }, [query?.comments, shouldShowHistoryStatusComments]);

  const [addCommentToQueryState, addCommentToQuery] = useAddCommentToQueryMutation();
  const [addAnswerToQueryState, addAnswerToQuery] = useAddAnswerToQueryMutation();
  const [, requestClarification] = useRequestClarificationMutation();

  useEffect(() => {
    if (allowLeavingPage && commentText !== '') {
      setAllowLeavingPage(false);
    } else if (!allowLeavingPage && commentText === '') {
      setAllowLeavingPage(true);
    }
  }, [commentText]);

  useEffect(() => {
    commentText !== ''
      ? setAllowEditModeWithoutConfirmation(false)
      : setAllowEditModeWithoutConfirmation(true);
  }, [commentText]);

  useEffect(() => {
    if (editMode) {
      setCommentText('');
    }
  }, [editMode]);

  useEffect(() => {
    if (!allowLeavingPage) {
      window.addEventListener('beforeunload', leavingPageAlert);
      return () => {
        window.removeEventListener('beforeunload', leavingPageAlert);
      };
    }
    window.removeEventListener('beforeunload', leavingPageAlert);
    return undefined;
  }, [allowLeavingPage]);

  const leavingPageAlert = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
  };

  const handleConversationInput = (
    event: Ui5CustomEvent<TextAreaDomRef, { escapePressed?: boolean }>
  ): void => {
    setCommentText(event.target.value || '');
  };

  const onClickAddComment = async () => {
    if (isCommentTextInvalid()) {
      return;
    }
    const input: QueryCommentInput = {
      commentText,
      queryId: parseInt(query?.id ?? '')
    };

    const { data, error } = await addCommentToQuery({ input });

    if (data?.comment && !error) {
      setCommentText('');
      showResultMessage({
        isSuccess: true,
        message: t('display-query-comments-saved-successfully')
      });
      setIsUserMessageOpen(false);
    } else {
      showResultMessage({
        isSuccess: false,
        message: t('display-query-comments-error-saving')
      });
    }
  };

  const onConfirmAnswer = async () => {
    const input: QueryAnswerInput = {
      commentText,
      queryId: parseInt(query?.id ?? '')
    };

    const { data, error } = await addAnswerToQuery({ input });

    if (data?.query && !error) {
      setCommentText('');
      showResultMessage({
        isSuccess: true,
        message: t('display-query-comments-saved-successfully')
      });
      setIsUserMessageOpen(false);
    } else {
      showResultMessage({
        isSuccess: false,
        message: t('display-query-comments-error-saving')
      });
    }
  };

  const onConfirmRequestClarification = async () => {
    const input: RequestClarificationInput = {
      commentText,
      queryId: parseInt(query?.id ?? '')
    };

    const { data, error } = await requestClarification({ input });

    if (data?.query && !error) {
      setCommentText('');
      showResultMessage({
        isSuccess: true,
        message: t('display-query-comments-saved-successfully')
      });
      setIsUserMessageOpen(false);
    } else {
      showResultMessage({
        isSuccess: false,
        message: t('display-query-comments-error-saving')
      });
    }
  };

  const showResultMessage = (message: ResultUserMessage) => {
    setResultMessage(message);
    setIsUserMessageOpen(true);
  };

  const isCommentTextInvalid = () => {
    return commentText.trim() === '';
  };

  const isStatusEqual = (...status: Status[]) => status.some((s) => query?.status === s);

  const onChangeShowHistoryStatusComments = (event: CustomEvent) => {
    const { checked } = event.target as HTMLInputElement;
    setShouldShowHistoryStatusComments(checked);
  };

  const shouldDisplayAnswerButton = useMemo(() => {
    return !isStatusEqual(Status.CREATOR_TO_CLARIFY) && isInternalUser;
  }, [query?.status]);

  const shouldDisplayRequestClarificationButton = useMemo(() => {
    if (isInternalUser) {
      return isStatusEqual(Status.NEW, Status.ASSIGNED, Status.ANSWERED, Status.DISTRIBUTED);
    }

    const suppliersFromUser = suppliers.flatMap((supplier) => [
      t('supplier-name-format', { name: supplier.name, vendor: supplier.vendor }),
      supplier.vendor
    ]);
    const isDistributedToUserSupplier =
      distributions?.some((distribution) =>
        suppliersFromUser.includes(distribution.language.supplier)
      ) || false;

    return isStatusEqual(Status.DISTRIBUTED) && isDistributedToUserSupplier;
  }, [query?.status]);

  const confirmationDialogConfig: Record<
    CommentType.ANSWER | CommentType.REQUEST_CLARIFICATION,
    ConfirmationDialogProps
  > = {
    [CommentType.ANSWER]: {
      'data-testid': 'display-query-comments-confirm-dialog-answer',
      open: isConfirmDialogOpen.open === CommentType.ANSWER,
      type: MessageBoxType.Confirm,
      onClose: () => setIsConfirmDialogOpen({ open: undefined }),
      onConfirm: onConfirmAnswer,
      children: t('display-query-comments-confirm-dialog-answer')
    },
    [CommentType.REQUEST_CLARIFICATION]: {
      'data-testid': 'display-query-comments-confirm-dialog-request-clarification',
      open: isConfirmDialogOpen.open === CommentType.REQUEST_CLARIFICATION,
      type: MessageBoxType.Confirm,
      onClose: () => setIsConfirmDialogOpen({ open: undefined }),
      onConfirm: onConfirmRequestClarification,
      children: t('display-query-comments-confirm-dialog-request-clarification')
    }
  };

  return (
    <>
      <Prompt when={!allowLeavingPage} message={t('display-query-comments-leave-page-prompt')} />
      <FlexBox direction={FlexBoxDirection.Column}>
        <TextArea
          data-help-id="display-query-comments-text-area"
          className={classes.conversationInput}
          value={commentText}
          onInput={handleConversationInput}
          rows={6}
          disabled={editMode}
          placeholder={
            editMode
              ? t('display-query-comments-textarea-placeholder-on-edit-mode')
              : t('display-query-comments-textarea-placeholder')
          }
          data-testid="query-comments-textarea"
        />
        <div className={classes.buttonsContainer}>
          {shouldDisplayAnswerButton && (
            <Button
              data-help-id="display-query-comments-button-answer"
              disabled={isCommentTextInvalid() || editMode}
              onClick={() => setIsConfirmDialogOpen({ open: CommentType.ANSWER })}
              icon="lightbulb"
              design={ButtonDesign.Positive}
              className="button-bg-white"
            >
              {t('display-query-comments-button-answer')}
            </Button>
          )}
          {shouldDisplayRequestClarificationButton && (
            <Button
              data-help-id="display-query-comments-button-request-clarification"
              disabled={isCommentTextInvalid() || editMode}
              onClick={() => setIsConfirmDialogOpen({ open: CommentType.REQUEST_CLARIFICATION })}
              icon="question-mark"
              design={ButtonDesign.Attention}
            >
              {t('display-query-comments-button-request-clarification')}
            </Button>
          )}
          <Button
            data-help-id="display-query-comments-button-save"
            design={ButtonDesign.Default}
            onClick={onClickAddComment}
            disabled={isCommentTextInvalid() || editMode}
          >
            {t('display-query-comments-button-save')}
          </Button>
        </div>
      </FlexBox>
      {(addCommentToQueryState.fetching || addAnswerToQueryState.fetching) && (
        <BusyIndicator active />
      )}
      <HistoryCommentsSwitch
        checked={shouldShowHistoryStatusComments}
        onChange={onChangeShowHistoryStatusComments}
      />
      <Timeline
        data-help-id="display-query-comments-timeline"
        className={classes.conversationTimeline}
        data-testid="timeline-element"
      >
        {query?.comments && (
          <CustomTimeline isInternalUser={isInternalUser} comments={filteredComments} />
        )}
      </Timeline>
      {Object.entries(confirmationDialogConfig).map(([key, value]) => {
        return <ConfirmationDialog key={key} {...value} />;
      })}
      <UserMessage
        isVisible={isUserMessageOpen}
        resultMessage={resultMessage}
        onResetUserMessage={() => setIsUserMessageOpen(false)}
      />
    </>
  );
};
